/**
 * A/B tests. Tests come from the "Swyft test" metaobjects (snippets/swyft-tests.liquid); each test's own script asks
 * for an arm and reports an exposure when a qualifying product is shown. Only visitors who allow analytics are
 * enrolled; everyone else sees control and is never counted. The readout is Merlin's (desk/experiments.json).
 *
 * QA: ?swyft_qa=pdp-cutoff-v1:B forces an arm for this browser session, whatever the test's status, and marks
 * every event qa so it is never counted. ?swyft_qa=off clears it.
 */
(() => {
  const VID = '_swyft_vid';
  const TESTS = '_swyft_tests';
  const QA = '_swyft_qa';
  const YEAR = 365 * 86400;

  const config = (() => {
    try {
      return JSON.parse(document.getElementById('swyft-tests').textContent);
    } catch (e) {
      return { tests: [], copy: {} };
    }
  })();

  const getCookie = (name) => {
    const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  };
  const setCookie = (name, value, maxAge) => {
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; SameSite=Lax${maxAge ? `; max-age=${maxAge}` : ''}`;
  };

  // FNV-1a: stable across browsers, so a visitor's arm never depends on which device math they have.
  const bucket = (text) => {
    let h = 0x811c9dc5;
    for (let i = 0; i < text.length; i++) {
      h ^= text.charCodeAt(i);
      h = Math.imul(h, 0x01000193);
    }
    return (h >>> 0) % 100;
  };

  const qaArms = (() => {
    const param = new URLSearchParams(location.search).get('swyft_qa');
    if (param === 'off') setCookie(QA, '', -1);
    else if (param) setCookie(QA, param);
    const raw = param === 'off' ? null : param || getCookie(QA);
    const arms = {};
    for (const pair of (raw || '').split(',')) {
      const [id, arm] = pair.split(':');
      if (id && (arm === 'A' || arm === 'B')) arms[id] = arm;
    }
    return arms;
  })();
  const isQa = Object.keys(qaArms).length > 0;

  const today = () => new Date().toISOString().slice(0, 10);
  const live = (t) =>
    t.status === 'running' && (!t.start || t.start <= today()) && (!t.end || today() <= t.end);

  // One running test per layer. Two would share visitors, so neither enrols until one is stopped.
  const enrollable = (() => {
    const byLayer = {};
    for (const t of config.tests.filter(live)) (byLayer[t.layer] = byLayer[t.layer] || []).push(t);
    const out = {};
    for (const [layer, list] of Object.entries(byLayer)) {
      if (list.length === 1) out[list[0].id] = list[0];
      else console.warn(`[swyft-test] ${list.length} running tests in layer ${layer}; none enrolled`);
    }
    return out;
  })();

  const consent = new Promise((resolve) => {
    const check = () => {
      const p = window.Shopify && window.Shopify.customerPrivacy;
      if (p && typeof p.analyticsProcessingAllowed === 'function') return resolve(Boolean(p.analyticsProcessingAllowed()));
      return false;
    };
    if (check() !== false) return;
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (error || check() === false) resolve(false);
      });
    }
    setTimeout(() => resolve(false), 2000);
  });

  const visitorId = () => {
    let vid = getCookie(VID);
    if (!vid) {
      vid = window.crypto && crypto.randomUUID ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      setCookie(VID, vid, YEAR);
    }
    return vid;
  };

  /** 'A', 'B', or null when this visitor is not in the test (not running, no consent): show control, log nothing. */
  async function arm(testId) {
    if (qaArms[testId]) return qaArms[testId];
    const t = enrollable[testId];
    if (!t || !(await consent)) return null;
    return bucket(`${visitorId()}:${testId}`) < t.shareB ? 'B' : 'A';
  }

  const seen = (key) => {
    try {
      if (localStorage.getItem(key)) return true;
      localStorage.setItem(key, '1');
    } catch (e) {}
    return false;
  };

  /** The visitor was shown a qualifying product. Once per test, arm, variant and day; ties the cart to the arm. */
  async function exposure(testId, armName, detail) {
    if (!armName) return;
    if (seen(`swyft_exp:${testId}:${armName}:${detail.variant_id}:${today()}`)) return;
    const qa = Boolean(qaArms[testId]);
    const payload = { test: testId, arm: armName, vid: qa ? 'qa' : visitorId(), qa, ...detail };
    try {
      window.Shopify.analytics.publish('swyft_test_exposure', payload);
    } catch (e) {}
    try {
      window.clarity = window.clarity || function () { (window.clarity.q = window.clarity.q || []).push(arguments); };
      window.clarity('set', testId, armName);
    } catch (e) {}
    if (qa) return;
    const assigned = (getCookie(TESTS) || '').split(',').filter((x) => x && !x.startsWith(`${testId}:`));
    assigned.push(`${testId}:${armName}`);
    const value = assigned.join(',');
    if (value === getCookie(TESTS) && sessionStorage.getItem('swyft_cart_tagged') === value) return;
    setCookie(TESTS, value, YEAR);
    try {
      // The underscore keeps both off the customer's order confirmation.
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { _swyft_vid: payload.vid, _swyft_tests: value } }),
      });
      sessionStorage.setItem('swyft_cart_tagged', value);
    } catch (e) {}
  }

  /** Only the hold class, for a test that has decided its element. */
  const release = () => document.documentElement.classList.remove('swyft-hold');

  // Server clock, because a shopper's clock can be hours out and a deadline must be the van's, not theirs.
  let offset = null;
  async function now() {
    if (offset === null) {
      try {
        const t0 = Date.now();
        const res = await fetch('/cart.js', { cache: 'no-store' });
        const t1 = Date.now();
        const server = Date.parse(res.headers.get('date'));
        offset = Number.isNaN(server) ? 0 : server + 500 - (t0 + t1) / 2;
      } catch (e) {
        offset = 0;
      }
    }
    return Date.now() + offset;
  }

  window.SwyftTest = { arm, exposure, release, now, copy: config.copy || {}, qa: isQa };
  if (!isQa && Object.keys(enrollable).length === 0) release();
})();
